package com.writerslab

import kotlinx.browser.localStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

/**
 * Frozen snapshot of the per-user UI state that's safe to paint
 * synchronously on app launch. Lets the page render with «last known
 * good» values before /me + /history come back, eliminating the
 * default-then-real flicker on every reload.
 *
 * What lives here vs what doesn't:
 *  - Lightweight, idempotent-to-show values only: counters, history
 *    rows, the user's display name. Authentication tokens go through
 *    [AppDataStore]'s token slots (different lifecycle: rotated by
 *    Keycloak refresh).
 *  - Per-book heavy data (static analysis JSON, AI reports) is NOT
 *    cached here — that's the existing sessionStorage handoff
 *    (`wl_analysis`) for the sign-in flow.
 *  - The snapshot is *tied to a specific user id*. On load we compare
 *    the stored `userId` against [AppDataStore.getUserId] (populated
 *    from the most recent /me); mismatch → discard. This is the guard
 *    against "user A's browser, user B just signed in" leaking A's
 *    credits onto B's screen.
 */
@Serializable
data class UserCacheSnapshot(
    val userId: String,
    val email: String? = null,
    val credits: Int = 0,
    val freeShortRemaining: Int = 0,
    val freeShortCharThreshold: Int = 30000,
    val freeAnnotationsRemaining: Int = 0,
    val freeCoverBatchesRemaining: Int = 0,
    val freeTeaserPreviewsRemaining: Int = 0,
    val debugTopUp: Boolean = false,
    /** Mirrors `UserInfo.emailVerified` from `/me`. Cached so the
     *  verification banner doesn't flash «not verified» on every
     *  reload before the backend reply lands; the fresh /me still
     *  runs in the background and overrides this within ~300ms. */
    val emailVerified: Boolean = false,
    /** Mirrors `UserInfo.canEditText`, so «Править текст» isn't shown
     *  locked for a moment on every reload. */
    val canEditText: Boolean = false,
    val bookHistory: List<BookHistoryItem> = emptyList(),
    /** Last-opened book — `id`, file name and the *raw JSON* of its
     *  static analysis. Stored as a JSON string (not a parsed
     *  AnalysisResult) so we don't have to mark a deep tree of model
     *  types `@Serializable`; we reuse `BookRepository.parseAnalysisJson`
     *  on read. All three null → no book to restore (anonymous fresh
     *  visitor / after `reset()`). */
    val currentBookId: String? = null,
    val currentFileName: String? = null,
    val currentAnalysisJson: String? = null,
    /** Promo-job running flags. Pre-set on page load so generate buttons
     *  are disabled during the reattachPromoJobs round-trip, preventing
     *  accidental double-triggers. Reset to false if the backend doesn't
     *  confirm a matching active job (registry cleared on restart). */
    val annotationRunning: Boolean = false,
    val coverBatchRunning: Boolean = false,
    val teaserRunning: Boolean = false,
    /** Wall-clock millis when this snapshot was written. Currently only
     *  used for debug logging; could later drive «too old, skip cache»
     *  if we ever cap freshness. */
    val savedAtMs: Double = 0.0,
)

object UserCache {
    private const val KEY = "wl_user_cache"
    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Load whatever's in localStorage. Returns null if:
     *  - nothing was ever saved
     *  - the stored userId doesn't match [AppDataStore.getUserId]
     *    (different user signed in on this browser, OR we're between
     *    a fresh login and the first /me — the id slot is cleared on
     *    every callback so a stale snapshot doesn't leak)
     *  - the JSON is corrupt (schema drift between versions, etc.)
     *
     * Safe to call synchronously at app construction — pure localStorage
     * read, no network.
     */
    fun load(): UserCacheSnapshot? {
        val raw = localStorage.getItem(KEY) ?: return null
        val parsed = try {
            json.decodeFromString<UserCacheSnapshot>(raw)
        } catch (_: SerializationException) {
            // Schema drift / corruption — wipe so a fresh write can land.
            localStorage.removeItem(KEY)
            return null
        }
        val current = AppDataStore.getUserId()
        if (current == null || parsed.userId != current) {
            // Either we don't yet know who we are (anon / between login
            // and first /me), or this cache belongs to a different user.
            // Don't even consider it.
            return null
        }
        return parsed
    }

    fun save(snapshot: UserCacheSnapshot) {
        localStorage.setItem(KEY, json.encodeToString(snapshot))
    }

    fun clear() {
        localStorage.removeItem(KEY)
    }
}
